import { ProjectRole } from "@/enums/ProjectRole";
import type { ProjectEntity } from "@/types/ProjectEntity";
import type { ProjectSettings } from "@/types/Projects";
import type { EntityRetainage, Retainage } from "@/types/Retainage";
import { round } from "./Utility";

export const retain = (
  indexes: number[],
  entityRetainageList: EntityRetainage[],
  sourceOfFunds: ProjectEntity[],
  retainLender: boolean,
  retainBorrower: boolean,
  retainagePercent: number,
  projectSettings: ProjectSettings
) => {
  const lenderEntityRetainageList = entityRetainageList.filter((entityRetainage: EntityRetainage) =>
    entityRetainage.projectEntityDisplay.startsWith("L")
  );
  const borrowerEntityRetainageList = entityRetainageList.filter((entityRetainage: EntityRetainage) =>
    entityRetainage.projectEntityDisplay.startsWith("B")
  );

  if (projectSettings.enableLenderParticipation && projectSettings.participationMethod == "RANK") {
    sourceOfFunds.forEach((source: ProjectEntity) => {
      if ((source.type.includes(ProjectRole.LENDER) && retainLender) || (source.type.includes(ProjectRole.BORROWER) && retainBorrower)) {
        if (indexes.length === entityRetainageList[0].retainageList.length) {
          //If retaining all just set to 0
          source.totalFundsRetained = 0;
        } else {
          const entityRetainage: EntityRetainage = entityRetainageList.filter(
            (entityRetainage: EntityRetainage) => source.projectEntityId === entityRetainage.projectEntityId
          )[0];
          let totalFundsRetained = 0;
          for (let i = 0; i < entityRetainageList[0].retainageList.length; i++) {
            if (!indexes.includes(i)) totalFundsRetained += entityRetainage.retainageList[i].retainageThisDraw;
          }
          source.totalFundsRetained = totalFundsRetained;
        }
      }
    });
  }

  for (let i = 0; i < indexes.length; i++) {
    if (retainLender) {
      retainItem(indexes[i], lenderEntityRetainageList, sourceOfFunds, retainagePercent, projectSettings, ProjectRole.LENDER);
    }

    if (retainBorrower) {
      retainItem(indexes[i], borrowerEntityRetainageList, sourceOfFunds, retainagePercent, projectSettings, ProjectRole.BORROWER);
    }
  }
};

const retainItem = (
  index: number,
  entityRetainageList: EntityRetainage[],
  sourceOfFunds: ProjectEntity[],
  retainagePercent: number,
  projectSettings: ProjectSettings,
  type: ProjectRole
) => {
  if (entityRetainageList[0].retainageList[index].description.toUpperCase() === "~~INTEREST PAYMENT") return;

  let drawAmount =
    type === ProjectRole.LENDER
      ? entityRetainageList[0].retainageList[index].thisDrawLoan
      : entityRetainageList[0].retainageList[index].thisDrawEquity;
  if (drawAmount === 0) return;

  let fundsToRetain = round(drawAmount * retainagePercent, 2);

  if (projectSettings.enableLenderParticipation && projectSettings.participationMethod == "PERCENT") {
    entityRetainageList.forEach((entityRetainage: EntityRetainage) => {
      const projectEntity: ProjectEntity = sourceOfFunds.filter(
        (source: ProjectEntity) => source.projectEntityId === entityRetainage.projectEntityId
      )[0];
      const retainage: Retainage = entityRetainage.retainageList[index];
      retainage.retainageThisDraw = round(fundsToRetain * (projectEntity.participationPercent / 100), 2);
      updateRetainageTotals(retainage);
    });
  } else if (projectSettings.enableLenderParticipation && projectSettings.participationMethod == "RANK") {
    retainByRank(index, entityRetainageList, sourceOfFunds, retainagePercent, type, fundsToRetain);
  } else {
    const retainage: Retainage = entityRetainageList[0].retainageList[index];
    retainage.retainageThisDraw = fundsToRetain;
    updateRetainageTotals(retainage);
  }
};

const retainByRank = (
  index: number,
  entityRetainageList: EntityRetainage[],
  sourceOfFunds: ProjectEntity[],
  retainagePercent: number,
  type: ProjectRole,
  fundsToRetain: number
) => {
  const sourceOfFundsByRank: ProjectEntity[] = sourceOfFunds
    .filter((source: ProjectEntity) => source.type.includes(type))
    .sort((a, b) => a.participationRank - b.participationRank);

  let sourceIndex = 0;
  do {
    const source = sourceOfFundsByRank[sourceIndex];
    const entityRetainage: EntityRetainage = entityRetainageList.filter(
      (entityRetainage: EntityRetainage) => entityRetainage.projectEntityId === source.projectEntityId
    )[0];
    const maxRetainage = round(source.fundsDisbursed * retainagePercent, 2);

    if (source.totalFundsRetained + fundsToRetain <= maxRetainage || sourceIndex === sourceOfFundsByRank.length - 1) {
      const retainage: Retainage = entityRetainage.retainageList[index];
      retainage.retainageThisDraw = fundsToRetain;
      updateRetainageTotals(retainage);
      source.totalFundsRetained += fundsToRetain;
      fundsToRetain = 0;
    } else {
      let retain = maxRetainage - source.totalFundsRetained;
      const retainage: Retainage = entityRetainage.retainageList[index];
      retainage.retainageThisDraw = retain;
      updateRetainageTotals(retainage);
      source.totalFundsRetained += retain;
      fundsToRetain = fundsToRetain - retain;
    }

    sourceIndex++;
  } while (fundsToRetain > 0);
};

export const release = (indexes: number[], entityRetainage: EntityRetainage) => {
  for (let i = 0; i < indexes.length; i++) {
    const retainage: Retainage = entityRetainage.retainageList[indexes[i]];
    retainage.releasedThisDraw += retainage.retainageBalance;
    updateRetainageTotals(retainage);
  }
};

export const updateRetainageTotals = (retainage: Retainage) => {
  retainage.totalRetainage = round(retainage.retainageThisDraw + retainage.totalRetainagePriorDraw, 2);
  retainage.totalReleased = round(retainage.totalReleasedPriorDraw + retainage.releasedThisDraw, 2);
  retainage.retainageBalance = round(retainage.totalRetainage - retainage.totalReleased, 2);
};
